import { CT_THRESHOLDS, CURVE_COLORS } from '@resistapp/client/components/plots/chip-view-constant';
import { KeysPressOptions } from '@resistapp/client/components/plots/legends/legend';
import { Curve } from '@resistapp/client/components/plots/simple-line-chart';
import { LocalStorageId } from '@resistapp/client/components/shared/local-storage';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import { useChipDebug } from '@resistapp/client/hooks/api';
import { EnvironmentSubType } from '@resistapp/common/environment-types';
import { assessTechReps } from '@resistapp/common/qa';
import { availableFlags, Chip, SmartChipFlag } from '@resistapp/common/types';
import { groupBy, keyBy, uniq, values } from 'lodash';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import useLocalStorageState from 'use-local-storage-state';

interface HeatmapDatum {
  row: number;
  column: number;
  assay: string;
  sample: string;
  sampleName: string | null;
  ct: number | null;
  tm: number | null;
  efficiency: number | null;
  flags: string | null;
  selectedAssays: string[] | null;
  rejectRule: number | null;
}

interface ChipContextData {
  loading: boolean;
  chipInfo: Chip | undefined;
  projects: Array<{ id: number; name: string }>;
  selectedSamples: string[] | null;
  selectedGroups: string[] | null;
  sampleKeys: string[];
  uniqueGroups: string[];
  assayColorByAssay: Record<string, string>;
  sampleColorByKey: Record<string, string>;
  colorMode: 'ASSAY' | 'SAMPLE';
  heatmapData: HeatmapDatum[];
  ampCurves: Curve[];
  derCurves: Curve[];
  flaggedAssays: string[];
  flaggedSamples: string[];
  sampleLegendItems: Array<{ label: string; displayLabel: string; color: string }>;
  selectedMetric: 'ct' | 'efficiency';
  setSelectedMetricStable: React.Dispatch<React.SetStateAction<'ct' | 'efficiency'>>;
  highlightFlag: 'None' | SmartChipFlag;
  setHighlightFlagStable: React.Dispatch<React.SetStateAction<'None' | SmartChipFlag>>;
  flagOptions: Array<{ label: string; value: 'None' | SmartChipFlag }>;
  showRejected: boolean;
  setShowRejectedStable: React.Dispatch<React.SetStateAction<boolean>>;
  toggleSampleStable: (label: string, keys: KeysPressOptions) => void;
  toggleGroupStable: (assay: string, keys: KeysPressOptions) => void;
  handleCellClickStable: (sample: string, assay: string, keys: KeysPressOptions, isSelected: boolean) => void;
  handleSelectBioSampleStable: (baseSampleId: string) => void;
}

const ChipContext = createContext<ChipContextData | undefined>(undefined);

export function ChipContextProvider({ children }: { children: React.ReactNode }) {
  const { chipId } = useParams();
  const { searchParams, setSearchParamsStable } = useSearchParamsContext();
  const { data: debugResp, isLoading } = useChipDebug(chipId || '');

  const { debugData, chipInfo, projects } = useMemo(() => {
    return {
      debugData: debugResp?.debugData ?? [],
      chipInfo: debugResp?.chip,
      projects: debugResp?.projects ?? [],
    };
  }, [debugResp]);

  const selectedSamplesParam = searchParams.get('samples');
  const selectedGroupsParam = searchParams.get('ggroups');
  const selectedSamples = useMemo(
    () => (selectedSamplesParam ? selectedSamplesParam.split(',') : null),
    [selectedSamplesParam],
  );
  const selectedGroups = useMemo(
    () => (selectedGroupsParam ? selectedGroupsParam.split(',') : null),
    [selectedGroupsParam],
  );

  const uniqueGroups = useMemo(() => Array.from(new Set(debugData.map(d => d.cts.assay))), [debugData]);
  const sampleKeys = useMemo(() => Array.from(new Set(debugData.map(d => d.cts.sample))), [debugData]);

  const defaultColor = CURVE_COLORS[0];
  const dynamicColors = CURVE_COLORS.slice(1);

  const getSampleId = useCallback((sampleKey: string) => sampleKey.replace(/-rep\d+$/, ''), []);

  const colorAssignments = useMemo(() => {
    const sampleColor: Record<string, string> = {};
    const assayColor: Record<string, string> = {};
    const selectedGroupsCount = selectedGroups ? selectedGroups.length : 0;
    const oneAssaySelected = selectedGroupsCount === 1;
    const assaysToColor = oneAssaySelected ? selectedGroups ?? [] : selectedGroups ?? uniqueGroups;

    const maxColors = dynamicColors.length;

    // -------------------- ASSAY COLOURING --------------------
    if (!oneAssaySelected) {
      // If we have more assays to colour than colours available, fall back to default for all.
      if (assaysToColor.length > maxColors) {
        assaysToColor.forEach(assay => {
          assayColor[assay] = defaultColor;
        });
      } else {
        assaysToColor.forEach((assay, idx) => {
          assayColor[assay] = dynamicColors[idx] ?? defaultColor;
        });
      }

      // In assay mode samples always get default colour.
      sampleKeys.forEach(k => {
        sampleColor[k] = defaultColor;
      });
    } else {
      // Ensure the (single) selected assay uses default colour
      if (selectedGroups) {
        const selectedAssay = selectedGroups[0];
        assayColor[selectedAssay] = defaultColor;
      }

      // Determine which sample keys we should consider for colouring.
      const selectionKeys = selectedSamples && selectedSamples.length > 0 ? selectedSamples : sampleKeys;
      const sampleIds = uniq(selectionKeys.map(getSampleId));

      // ---------------- Replicates within a single biological sample ----------------
      if (sampleIds.length === 1) {
        const repsKeys = sampleKeys.filter(k => getSampleId(k) === sampleIds[0]);
        repsKeys.forEach(k => {
          const match = k.match(/-rep(\d+)$/);
          const repIdx = match ? parseInt(match[1], 10) - 1 : -1;
          sampleColor[k] = repIdx >= 0 && repIdx < maxColors ? dynamicColors[repIdx] : defaultColor;
        });
        sampleKeys
          .filter(k => !repsKeys.includes(k))
          .forEach(k => {
            sampleColor[k] = defaultColor;
          });
      }
      // ---------------- Multiple biological samples ----------------
      else if (sampleIds.length <= maxColors) {
        // We have enough colours -> colour each bio-sample individually.
        sampleIds.forEach((id, idx) => {
          const color = dynamicColors[idx] ?? defaultColor;
          sampleKeys
            .filter(k => getSampleId(k) === id)
            .forEach(k => {
              sampleColor[k] = color;
            });
        });
        // Samples not in the current selection (if a subset is selected) stay default.
        sampleKeys
          .filter(k => !selectionKeys.includes(k))
          .forEach(k => {
            sampleColor[k] = defaultColor;
          });
      } else {
        // Too many samples – fall back to default for all.
        sampleKeys.forEach(k => {
          sampleColor[k] = defaultColor;
        });
      }
    }

    sampleKeys.forEach(k => {
      if (!sampleColor[k]) sampleColor[k] = defaultColor;
    });
    uniqueGroups.forEach(assay => {
      if (!assayColor[assay]) assayColor[assay] = defaultColor;
    });

    const mode = !oneAssaySelected ? 'ASSAY' : 'SAMPLE';
    return { sampleColor, assayColor, mode } as const;
  }, [selectedGroups, uniqueGroups, sampleKeys, dynamicColors, defaultColor, selectedSamples, getSampleId]);

  type ParamName = 'samples' | 'ggroups';
  const setParamListStable = useCallback(
    (param: ParamName, list: string[] | null) => {
      setSearchParamsStable(
        prev => {
          const next = new URLSearchParams(prev);
          if (!list || list.length === 0) {
            next.delete(param);
          } else {
            next.set(param, list.join(','));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParamsStable],
  );

  const lastSampleRef = useRef<string | null>(null);
  const lastAssayRef = useRef<string | null>(null);

  const updateParamSelectionStable = useCallback(
    (param: ParamName, list: string[] | null) => {
      setParamListStable(param, list);
    },
    [setParamListStable],
  );

  const computeRangeSelection = (all: string[], a: string, b: string) => {
    const idxA = all.indexOf(a);
    const idxB = all.indexOf(b);
    if (idxA === -1 || idxB === -1) return [b];
    const [start, end] = idxA < idxB ? [idxA, idxB] : [idxB, idxA];
    return all.slice(start, end + 1);
  };

  const toggleSampleStable = useCallback(
    (label: string, keys: KeysPressOptions) => {
      const all = sampleKeys;
      const currentExplicit = selectedSamples;
      if (keys.shift) {
        const anchor = lastSampleRef.current ?? label;
        const range = computeRangeSelection(all, anchor, label);
        const list = range.length === all.length ? null : range;
        updateParamSelectionStable('samples', list);
        lastSampleRef.current = label;
        return;
      }
      if (keys.ctrl) {
        const current = currentExplicit ?? all;
        const next = current.includes(label) ? current.filter(s => s !== label) : [...current, label];
        const list = next.length === 0 || next.length === all.length ? null : next;
        updateParamSelectionStable('samples', list);
        lastSampleRef.current = label;
        return;
      }
      if (currentExplicit && currentExplicit.length === 1 && currentExplicit[0] === label) {
        updateParamSelectionStable('samples', null);
      } else {
        updateParamSelectionStable('samples', [label]);
      }
      lastSampleRef.current = label;
    },
    [selectedSamples, sampleKeys, updateParamSelectionStable],
  );

  const toggleGroupStable = useCallback(
    (assay: string, keys: KeysPressOptions) => {
      const all = uniqueGroups;
      const currentExplicit = selectedGroups;
      if (keys.shift) {
        const anchor = lastAssayRef.current ?? assay;
        const range = computeRangeSelection(all, anchor, assay);
        const list = range.length === all.length ? null : range;
        updateParamSelectionStable('ggroups', list);
        lastAssayRef.current = assay;
        return;
      }
      if (keys.ctrl) {
        const current = currentExplicit ?? all;
        const next = current.includes(assay) ? current.filter(a => a !== assay) : [...current, assay];
        const list = next.length === 0 || next.length === all.length ? null : next;
        updateParamSelectionStable('ggroups', list);
        lastAssayRef.current = assay;
        return;
      }
      if (currentExplicit && currentExplicit.length === 1 && currentExplicit[0] === assay) {
        updateParamSelectionStable('ggroups', null);
      } else {
        updateParamSelectionStable('ggroups', [assay]);
      }
      lastAssayRef.current = assay;
    },
    [selectedGroups, uniqueGroups, updateParamSelectionStable],
  );

  const handleCellClickStable = useCallback(
    (sample: string, assay: string, keys: KeysPressOptions, isSelected: boolean) => {
      if (keys.ctrl) {
        if (isSelected) {
          const onlySampleSelectedCtrl = selectedSamples?.length === 1 && selectedSamples[0] === sample;
          const onlyAssaySelectedCtrl = selectedGroups?.length === 1 && selectedGroups[0] === assay;
          if (onlySampleSelectedCtrl && onlyAssaySelectedCtrl) {
            return;
          }
          const isLastSample = selectedSamples ? selectedSamples.length === 1 && selectedSamples[0] === sample : false;
          if (isLastSample) {
            toggleGroupStable(assay, { ctrl: true, shift: false });
          } else {
            toggleSampleStable(sample, { ctrl: true, shift: false });
          }
          return;
        }
        const sampleAlreadySelected = !selectedSamples || selectedSamples.includes(sample);
        const assayAlreadySelected = !selectedGroups || selectedGroups.includes(assay);
        if (sampleAlreadySelected && !assayAlreadySelected) {
          toggleGroupStable(assay, { ctrl: true, shift: false });
        } else if (!sampleAlreadySelected && assayAlreadySelected) {
          toggleSampleStable(sample, { ctrl: true, shift: false });
        } else {
          setSearchParamsStable(
            prev => {
              const next = new URLSearchParams(prev);
              const currentSamplesParam = next.get('samples');
              const currentSamples = currentSamplesParam ? currentSamplesParam.split(',') : null;
              const allSamples = sampleKeys;
              const updatedSamples = (() => {
                if (!currentSamples) {
                  const arr = [...allSamples];
                  if (!arr.includes(sample)) arr.push(sample);
                  return arr;
                }
                if (currentSamples.includes(sample)) return currentSamples;
                return [...currentSamples, sample];
              })();
              if (updatedSamples.length === allSamples.length) {
                next.delete('samples');
              } else {
                next.set('samples', updatedSamples.join(','));
              }
              const currentAssaysParam = next.get('ggroups');
              const currentAssays = currentAssaysParam ? currentAssaysParam.split(',') : null;
              const updatedAssays = (() => {
                if (!currentAssays) {
                  const arr = [...uniqueGroups];
                  if (!arr.includes(assay)) arr.push(assay);
                  return arr;
                }
                if (currentAssays.includes(assay)) return currentAssays;
                return [...currentAssays, assay];
              })();
              if (updatedAssays.length === uniqueGroups.length) {
                next.delete('ggroups');
              } else {
                next.set('ggroups', updatedAssays.join(','));
              }
              return next;
            },
            { replace: true },
          );
        }
        return;
      }
      const onlySampleSelectedNo = selectedSamples?.length === 1 && selectedSamples[0] === sample;
      const onlyAssaySelectedNo = selectedGroups?.length === 1 && selectedGroups[0] === assay;
      if (onlySampleSelectedNo && onlyAssaySelectedNo) {
        setSearchParamsStable(
          prev => {
            const next = new URLSearchParams(prev);
            next.delete('samples');
            next.delete('ggroups');
            return next;
          },
          { replace: true },
        );
      } else {
        setSearchParamsStable(
          prev => {
            const next = new URLSearchParams(prev);
            next.set('samples', sample);
            next.set('ggroups', assay);
            return next;
          },
          { replace: true },
        );
      }
      lastSampleRef.current = sample;
      lastAssayRef.current = assay;
    },
    [
      selectedSamples,
      selectedGroups,
      toggleSampleStable,
      toggleGroupStable,
      setSearchParamsStable,
      sampleKeys,
      uniqueGroups,
    ],
  );

  const isSampleVisible = useCallback(
    (sample: string) => (selectedSamples ? selectedSamples.includes(sample) : true),
    [selectedSamples],
  );
  const isGroupVisible = useCallback(
    (assay: string) => (selectedGroups ? selectedGroups.includes(assay) : true),
    [selectedGroups],
  );

  const visibleData = useMemo(
    () => debugData.filter(d => isSampleVisible(d.cts.sample) && isGroupVisible(d.cts.assay)),
    [debugData, isSampleVisible, isGroupVisible],
  );

  const sampleLegendItems = useMemo(() => {
    return sampleKeys.map(key => {
      const sampleName = debugData.find(d => d.cts.sample === key)?.sampleName || '';
      return { label: key, displayLabel: `${key} - ${sampleName}`, color: colorAssignments.sampleColor[key] };
    });
  }, [sampleKeys, debugData, colorAssignments.sampleColor]);

  const { allAssays } = useAssayContext();
  const assayInfoByAssay = useMemo(() => keyBy(allAssays, a => a.assay), [allAssays]);

  const rejectionById = useMemo(() => {
    const grouped = groupBy(debugData, d => `${d.cts.assay}|${getSampleId(d.cts.sample)}`);
    const map: Record<number, number | null> = {};
    values(grouped).forEach(cells => {
      if (!cells.length) return;
      const assay = cells[0].cts.assay;
      const info = assayInfoByAssay[assay];
      const tmMin = info.minMeltingTemperature ?? null;
      const tmMax = info.maxMeltingTemperature ?? null;
      const qa = assessTechReps(
        cells.map(c => ({ ct: c.cts.ct, efficiency: c.cts.efficiency, tm: c.cts.tm })),
        assay,
        tmMin,
        tmMax,
        CT_THRESHOLDS[5],
      );
      qa.reps.forEach((rep, idx) => {
        map[cells[idx].cts.id] = rep.rule;
      });
    });
    return map;
  }, [debugData, assayInfoByAssay, getSampleId]);

  const heatmapData = useMemo(
    () =>
      debugData.map(d => ({
        row: d.cts.row,
        column: d.cts.column,
        assay: d.cts.assay,
        sample: d.cts.sample,
        sampleName: d.sampleName,
        ct: d.cts.ct,
        tm: d.cts.tm,
        efficiency: d.cts.efficiency,
        flags: d.cts.flags,
        selectedAssays: selectedGroups,
        rejectRule: rejectionById[d.cts.id] ?? null,
      })),
    [debugData, selectedGroups, rejectionById],
  );

  const flaggedAssays = useMemo(() => {
    const byAssay = groupBy(debugData, d => d.cts.assay);
    return Object.entries(byAssay)
      .filter(([, cells]) => cells.every(c => c.cts.ct == null))
      .map(([assay]) => assay);
  }, [debugData]);

  const flaggedSamples = useMemo(() => {
    const bySample = groupBy(debugData, d => d.cts.sample);
    return Object.entries(bySample)
      .filter(([, cells]) => {
        const subtype = cells[0].sampleSubtype;
        if (subtype === EnvironmentSubType.NTC) return false;
        return cells.filter(c => c.cts.assay !== 'AY1').every(c => c.cts.ct == null);
      })
      .map(([sample]) => sample);
  }, [debugData]);

  const handleSelectBioSampleStable = useCallback(
    (baseSampleId: string) => {
      const reps = sampleKeys.filter(k => getSampleId(k) === baseSampleId);
      updateParamSelectionStable('samples', reps.length === sampleKeys.length ? null : reps);
    },
    [sampleKeys, getSampleId, updateParamSelectionStable],
  );

  const ampCurves: Curve[] = useMemo(
    () =>
      visibleData
        .map(d => {
          if (!d.amplificationCurve) return null;
          const curveColor =
            colorAssignments.mode === 'ASSAY'
              ? colorAssignments.assayColor[d.cts.assay]
              : colorAssignments.sampleColor[d.cts.sample];
          const curve: Curve = {
            xs: d.amplificationCurve.xs,
            ys: d.amplificationCurve.ys,
            color: curveColor,
            id: `${d.cts.sample}-amp-${d.cts.assay}-${d.cts.id}`,
            markerX: Number.isFinite(d.cts.ct) ? (d.cts.ct as number) : undefined,
            isRejected: rejectionById[d.cts.id] != null,
            isNotDetected: d.cts.ct == null,
          };
          return curve;
        })
        .filter(Boolean) as Curve[],
    [visibleData, colorAssignments.mode, colorAssignments.assayColor, colorAssignments.sampleColor, rejectionById],
  );

  const derCurves: Curve[] = useMemo(
    () =>
      visibleData
        .map(d => {
          if (!d.derivateCurve) return null;
          const curveColor =
            colorAssignments.mode === 'ASSAY'
              ? colorAssignments.assayColor[d.cts.assay]
              : colorAssignments.sampleColor[d.cts.sample];
          const curve: Curve = {
            xs: d.derivateCurve.xs,
            ys: d.derivateCurve.ys,
            color: curveColor,
            id: `${d.cts.sample}-der-${d.cts.assay}-${d.cts.id}`,
            markerX: Number.isFinite(d.cts.tm) ? (d.cts.tm as number) : undefined,
            isRejected: rejectionById[d.cts.id] != null,
            isNotDetected: d.cts.ct == null,
          };
          return curve;
        })
        .filter(Boolean) as Curve[],
    [visibleData, colorAssignments.mode, colorAssignments.assayColor, colorAssignments.sampleColor, rejectionById],
  );

  const [selectedMetric, setSelectedMetricStable] = useState<'ct' | 'efficiency'>('ct');

  type FlagOptionValue = 'None' | SmartChipFlag;
  const flagOptions = useMemo(() => {
    const opts = availableFlags.map((flag: SmartChipFlag) => ({ label: flag, value: flag }));
    return [...opts, { label: 'None', value: 'None' as const }];
  }, []);
  const [highlightFlag, setHighlightFlagStable] = useLocalStorageState<FlagOptionValue>(
    LocalStorageId.chipViewHighlightFlag,
    {
      defaultValue: 'None',
    },
  );
  const [showRejected, setShowRejectedStable] = useLocalStorageState<boolean>(
    LocalStorageId.chipViewShowRejected as string,
    {
      defaultValue: false,
    },
  );

  const contextValue: ChipContextData = {
    loading: isLoading,
    chipInfo,
    projects,
    selectedSamples,
    selectedGroups,
    sampleKeys,
    uniqueGroups,
    assayColorByAssay: colorAssignments.assayColor,
    sampleColorByKey: colorAssignments.sampleColor,
    colorMode: colorAssignments.mode,
    heatmapData,
    ampCurves,
    derCurves,
    flaggedAssays,
    flaggedSamples,
    sampleLegendItems,
    selectedMetric,
    setSelectedMetricStable,
    highlightFlag,
    setHighlightFlagStable,
    flagOptions,
    showRejected,
    setShowRejectedStable,
    toggleSampleStable,
    toggleGroupStable,
    handleCellClickStable,
    handleSelectBioSampleStable,
  };

  return <ChipContext.Provider value={contextValue}>{children}</ChipContext.Provider>;
}

export function useChipContext() {
  const ctx = useContext(ChipContext);
  if (!ctx) {
    throw new Error('useChipContext must be used within a ChipContextProvider');
  }
  return ctx;
}
